import { styled } from '@mui/material/styles';
import { COLOR } from '@src/styles/color';
import { FADE_IN_UP, FADE_IN_UP_DURATION } from '@src/styles/animation';

export const StyledTeachingVoices = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .voices-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .voices-title-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  .voices-see-more {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex-shrink: 0;
    cursor: pointer;
  }

  .voices-see-more-icon {
    width: 16px;
    height: 16px;
    color: ${COLOR.label2[64]};
  }

  .voices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(290px, 100%), 1fr));
    gap: 16px;
  }

  /* On the children, so each one animates as it mounts. */
  .voices-grid > * {
    animation: ${FADE_IN_UP} ${FADE_IN_UP_DURATION} ease-out;
  }
`;

export const StyledVoiceItem = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 562.5px;
  cursor: pointer;
  box-sizing: border-box;
  background: rgba(244, 245, 247, 0.8);
  /* The resting card keeps a hairline outline; playing thickens it to a full
     stroke. Drawn as an inset shadow, not a border: the design puts the stroke
     inside the 64px card, whereas a border is added to the box — and Chrome
     rounds the 0.5px one up to a whole device pixel first, which pushed every
     card to 66px and the section to 204px instead of 200px. */
  box-shadow: ${({ active }) =>
    active
      ? `inset 0 0 0 1px ${COLOR.darkSlate}`
      : `inset 0 0 0 0.5px ${COLOR.indigo[16]}`};

  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: ${COLOR.indigo[8]};
  }

  .voice-avatar {
    width: 48px;
    height: 48px;
    border-radius: 9999px;
    object-fit: cover;
    /* Placeholder tone while the portrait loads. */
    background-color: lightgray;
    flex-shrink: 0;
  }

  /* One line of text against a 48px avatar, so it has to be centred rather
     than sitting at the top of the row. */
  .voice-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    flex: 1;
  }

  .voice-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .voice-action {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
  }

  /* Padded out to the same 48px box as the play button beside it, so the two
     read as a pair and the heart keeps its distance from the name. */
  .voice-favourite {
    padding: 12px;
  }

  .voice-favourite-icon {
    width: 24px;
    height: 24px;
    color: ${({ favourite }) => (favourite ? COLOR.red : COLOR.label2[48])};
  }

  .voice-play {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: ${({ active }) => (active ? COLOR.primary : COLOR.white)};
    transition: background-color 0.2s ease, transform 0.15s ease;
  }

  .voice-play:active {
    transform: scale(0.9);
  }

  .voice-play-icon {
    width: 24px;
    height: 24px;
    color: ${COLOR.label2[100]};
  }
`;

/* Same box as a real card, so the grid keeps its shape when the voices land. */
export const StyledVoiceItemSkeleton = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 562.5px;
  box-sizing: border-box;
  background: rgba(244, 245, 247, 0.8);
  /* Inset, for the same reason as the real card above. */
  box-shadow: inset 0 0 0 0.5px ${COLOR.indigo[16]};

  .skeleton-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    flex: 1;
  }

  /* The bar sits in a box as tall as the name it stands in for, so the card is
     the same height loaded or loading. */
  .skeleton-name {
    display: flex;
    align-items: center;
    height: 24px;
  }

  .skeleton-favourite {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
  }
`;
